import { get, post, patch, del } from '../httpMethods';
import { ApiResponse, PaginationMeta } from '../../types/api';

export interface PharmacySupplier {
  id: string;
  supplierCode: string;
  name: string;
  contactPerson?: string;
  phoneNumber?: string;
  email?: string;
  address?: string;
  city?: string;
  taxId?: string;
  drugLicenseNo?: string;
  paymentTermsDays: number;
  isActive: boolean;
  notes?: string;
  createdAt: string;
  deletedAt?: string;
}

export type PharmacyPurchaseOrderStatus =
  | 'DRAFT'
  | 'SENT'
  | 'PARTIALLY_RECEIVED'
  | 'RECEIVED'
  | 'CANCELLED';

export interface PharmacyPurchaseOrderItem {
  id: string;
  poId: string;
  drugId: string;
  drug?: { id: string; name: string; drugCode: string; unitOfMeasure?: string };
  orderedQuantity: number;
  receivedQuantity: number;
  unitCost: number;
  lineTotal: number;
  batchNumber?: string;
  expiryDate?: string;
}

export interface PharmacyPurchaseOrder {
  id: string;
  poNumber: string;
  supplierId: string;
  supplier?: PharmacySupplier;
  orderDate: string;
  expectedDelivery?: string;
  status: PharmacyPurchaseOrderStatus;
  subtotal: number;
  discount: number;
  taxAmount: number;
  totalAmount: number;
  invoiceNumber?: string;
  notes?: string;
  items?: PharmacyPurchaseOrderItem[];
  createdAt: string;
  deletedAt?: string;
}

export interface PharmacyGoodsReceiptItem {
  id: string;
  grnId: string;
  drugId: string;
  drug?: { id: string; name: string; drugCode: string };
  quantityReceived: number;
  /** Quantity already sent back to the supplier via purchase returns. */
  returnedQuantity?: number;
  unitCost: number;
  batchNumber: string;
  expiryDate?: string;
  batchId?: string;
}

export interface PharmacyGoodsReceipt {
  id: string;
  grnNumber: string;
  poId?: string;
  po?: PharmacyPurchaseOrder;
  supplierId: string;
  supplier?: PharmacySupplier;
  receivedDate: string;
  invoiceNumber?: string;
  notes?: string;
  items?: PharmacyGoodsReceiptItem[];
  totalAmount?: number;
  itemCount?: number;
  invoice?: PharmacySupplierInvoice | null;
  returns?: PharmacyPurchaseReturn[];
  createdAt: string;
  deletedAt?: string;
}

export type PharmacySupplierInvoiceStatus = 'UNPAID' | 'PARTIAL' | 'PAID' | 'OVERDUE';

export interface PharmacySupplierInvoice {
  id: string;
  invoiceNumber: string;
  supplierId: string;
  supplier?: PharmacySupplier;
  grnId?: string;
  /** Goods receipt (GRN) this invoice was generated from, when loaded. */
  grn?: { id: string; grnNumber: string; receivedDate?: string };
  poId?: string;
  invoiceDate: string;
  dueDate?: string;
  amount: number;
  paidAmount: number;
  /** Value of purchase returns credited against this invoice. */
  creditedAmount?: number;
  status: PharmacySupplierInvoiceStatus;
  notes?: string;
  balance?: number;
  isOverdue?: boolean;
  createdAt: string;
}

export type PharmacyPaymentMethod =
  | 'CASH'
  | 'CARD'
  | 'BANK_TRANSFER'
  | 'CHEQUE'
  | 'MOBILE_BANKING'
  | 'OTHER';

export interface PharmacySupplierPayment {
  id: string;
  invoiceId: string;
  invoice?: PharmacySupplierInvoice;
  amount: number;
  paidAt: string;
  method: PharmacyPaymentMethod;
  reference?: string;
  paidById?: string;
  paidBy?: { id: string; firstName: string; lastName: string };
  notes?: string;
  createdAt: string;
}

export interface CreatePharmacyPurchaseOrderDto {
  supplierId: string;
  orderDate?: string;
  expectedDelivery?: string;
  status?: PharmacyPurchaseOrderStatus;
  discount?: number;
  taxAmount?: number;
  notes?: string;
  items: Array<{
    drugId: string;
    orderedQuantity: number;
    unitCost: number;
    batchNumber?: string;
    expiryDate?: string;
    notes?: string;
  }>;
}

export interface CreatePharmacyGoodsReceiptDto {
  poId?: string;
  supplierId: string;
  receivedDate?: string;
  invoiceNumber?: string;
  notes?: string;
  /** When true, automatically generate the supplier invoice (payable) from this receipt. */
  createInvoice?: boolean;
  items: Array<{
    poItemId?: string;
    drugId: string;
    quantityReceived: number;
    unitCost: number;
    batchNumber: string;
    expiryDate?: string;
  }>;
}

export interface GeneratePharmacyInvoiceFromGrnDto {
  invoiceNumber?: string;
  invoiceDate?: string;
  dueDate?: string;
  notes?: string;
}

export interface PharmacyPurchaseReturnItem {
  id: string;
  returnId: string;
  grnItemId: string;
  drugId?: string;
  batchId?: string;
  itemName: string;
  quantity: number;
  unitCost: number;
  refundAmount: number;
}

export interface PharmacyPurchaseReturn {
  id: string;
  returnNumber: string;
  supplierId: string;
  supplier?: PharmacySupplier;
  grnId?: string;
  totalAmount: number;
  creditedAmount: number;
  refundMethod?: PharmacyPaymentMethod;
  refundReference?: string;
  reason?: string;
  notes?: string;
  returnedAt: string;
  createdAt: string;
  items?: PharmacyPurchaseReturnItem[];
}

export interface CreatePharmacyPurchaseReturnItemDto {
  grnItemId: string;
  quantity: number;
}

export interface CreatePharmacyPurchaseReturnDto {
  items: CreatePharmacyPurchaseReturnItemDto[];
  returnedDate?: string;
  refundMethod?: PharmacyPaymentMethod;
  refundReference?: string;
  reason?: string;
  notes?: string;
}

export const pharmacyPurchasingService = {
  // Suppliers
  getSuppliers: (search?: string): Promise<ApiResponse<PharmacySupplier[]>> => {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    return get<PharmacySupplier[]>(`/pharmacy/suppliers${query}`);
  },
  getSupplier: (id: string): Promise<ApiResponse<PharmacySupplier>> =>
    get<PharmacySupplier>(`/pharmacy/suppliers/${id}`),
  createSupplier: (data: Partial<PharmacySupplier>): Promise<ApiResponse<PharmacySupplier>> =>
    post<PharmacySupplier>('/pharmacy/suppliers', data),
  updateSupplier: (id: string, data: Partial<PharmacySupplier>): Promise<ApiResponse<PharmacySupplier>> =>
    patch<PharmacySupplier>(`/pharmacy/suppliers/${id}`, data),
  deleteSupplier: (id: string): Promise<ApiResponse<void>> =>
    del<void>(`/pharmacy/suppliers/${id}`),
  getSuppliersPaginated: (
    page = 1,
    limit = 20,
    search?: string,
  ): Promise<ApiResponse<PharmacySupplier[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.append('search', search);
    return get<PharmacySupplier[]>(`/pharmacy/suppliers?${params.toString()}`) as Promise<
      ApiResponse<PharmacySupplier[]> & { meta: PaginationMeta }
    >;
  },
  bulkDeleteSuppliers: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; blocked: string[] }>> =>
    post<{ deleted: string[]; blocked: string[] }>('/pharmacy/suppliers/bulk-delete', { ids }),
  getSupplierTrash: (
    page = 1,
    limit = 20,
    search?: string,
  ): Promise<ApiResponse<PharmacySupplier[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.append('search', search);
    return get<PharmacySupplier[]>(`/pharmacy/suppliers/trash?${params.toString()}`) as Promise<
      ApiResponse<PharmacySupplier[]> & { meta: PaginationMeta }
    >;
  },
  restoreSupplier: (id: string): Promise<ApiResponse<PharmacySupplier>> =>
    post<PharmacySupplier>(`/pharmacy/suppliers/${id}/restore`),
  permanentDeleteSupplier: (id: string): Promise<ApiResponse<void>> =>
    del<void>(`/pharmacy/suppliers/${id}/permanent`),

  // Purchase orders
  getPurchaseOrders: (filters?: {
    status?: string;
    supplierId?: string;
  }): Promise<ApiResponse<PharmacyPurchaseOrder[]>> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.supplierId) params.append('supplierId', filters.supplierId);
    const query = params.toString() ? `?${params.toString()}` : '';
    return get<PharmacyPurchaseOrder[]>(`/pharmacy/purchase-orders${query}`);
  },
  getPurchaseOrder: (id: string): Promise<ApiResponse<PharmacyPurchaseOrder>> =>
    get<PharmacyPurchaseOrder>(`/pharmacy/purchase-orders/${id}`),
  createPurchaseOrder: (data: CreatePharmacyPurchaseOrderDto): Promise<ApiResponse<PharmacyPurchaseOrder>> =>
    post<PharmacyPurchaseOrder>('/pharmacy/purchase-orders', data),
  updatePurchaseOrder: (id: string, data: Partial<CreatePharmacyPurchaseOrderDto>): Promise<ApiResponse<PharmacyPurchaseOrder>> =>
    patch<PharmacyPurchaseOrder>(`/pharmacy/purchase-orders/${id}`, data),
  updatePurchaseOrderStatus: (id: string, status: PharmacyPurchaseOrderStatus): Promise<ApiResponse<PharmacyPurchaseOrder>> =>
    patch<PharmacyPurchaseOrder>(`/pharmacy/purchase-orders/${id}/status`, { status }),
  cancelPurchaseOrder: (id: string): Promise<ApiResponse<PharmacyPurchaseOrder>> =>
    post<PharmacyPurchaseOrder>(`/pharmacy/purchase-orders/${id}/cancel`),
  getPurchaseOrdersPaginated: (
    page = 1,
    limit = 20,
    filters?: { status?: string; supplierId?: string; search?: string },
  ): Promise<ApiResponse<PharmacyPurchaseOrder[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.append(key, String(value));
    });
    return get<PharmacyPurchaseOrder[]>(`/pharmacy/purchase-orders?${params.toString()}`) as Promise<
      ApiResponse<PharmacyPurchaseOrder[]> & { meta: PaginationMeta }
    >;
  },
  bulkDeletePurchaseOrders: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; blocked: string[] }>> =>
    post<{ deleted: string[]; blocked: string[] }>('/pharmacy/purchase-orders/bulk-delete', { ids }),
  getPurchaseOrderTrash: (
    page = 1,
    limit = 20,
    search?: string,
  ): Promise<ApiResponse<PharmacyPurchaseOrder[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.append('search', search);
    return get<PharmacyPurchaseOrder[]>(`/pharmacy/purchase-orders/trash?${params.toString()}`) as Promise<
      ApiResponse<PharmacyPurchaseOrder[]> & { meta: PaginationMeta }
    >;
  },
  restorePurchaseOrder: (id: string): Promise<ApiResponse<PharmacyPurchaseOrder>> =>
    post<PharmacyPurchaseOrder>(`/pharmacy/purchase-orders/${id}/restore`),
  permanentDeletePurchaseOrder: (id: string): Promise<ApiResponse<void>> =>
    del<void>(`/pharmacy/purchase-orders/${id}/permanent`),

  // Goods receipts
  getGoodsReceipts: (filters?: { supplierId?: string; poId?: string }): Promise<ApiResponse<PharmacyGoodsReceipt[]>> => {
    const params = new URLSearchParams();
    if (filters?.supplierId) params.append('supplierId', filters.supplierId);
    if (filters?.poId) params.append('poId', filters.poId);
    const query = params.toString() ? `?${params.toString()}` : '';
    return get<PharmacyGoodsReceipt[]>(`/pharmacy/goods-receipts${query}`);
  },
  getGoodsReceipt: (id: string): Promise<ApiResponse<PharmacyGoodsReceipt>> =>
    get<PharmacyGoodsReceipt>(`/pharmacy/goods-receipts/${id}`),
  createGoodsReceipt: (data: CreatePharmacyGoodsReceiptDto): Promise<ApiResponse<PharmacyGoodsReceipt>> =>
    post<PharmacyGoodsReceipt>('/pharmacy/goods-receipts', data),
  generateInvoiceFromGoodsReceipt: (
    grnId: string,
    data?: GeneratePharmacyInvoiceFromGrnDto,
  ): Promise<ApiResponse<PharmacySupplierInvoice>> =>
    post<PharmacySupplierInvoice>(`/pharmacy/goods-receipts/${grnId}/invoice`, data || {}),
  getGoodsReceiptsPaginated: (
    page = 1,
    limit = 20,
    filters?: { supplierId?: string; poId?: string; search?: string },
  ): Promise<ApiResponse<PharmacyGoodsReceipt[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.append(key, String(value));
    });
    return get<PharmacyGoodsReceipt[]>(`/pharmacy/goods-receipts?${params.toString()}`) as Promise<
      ApiResponse<PharmacyGoodsReceipt[]> & { meta: PaginationMeta }
    >;
  },
  bulkDeleteGoodsReceipts: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; blocked: string[] }>> =>
    post<{ deleted: string[]; blocked: string[] }>('/pharmacy/goods-receipts/bulk-delete', { ids }),
  getGoodsReceiptTrash: (
    page = 1,
    limit = 20,
    search?: string,
  ): Promise<ApiResponse<PharmacyGoodsReceipt[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.append('search', search);
    return get<PharmacyGoodsReceipt[]>(`/pharmacy/goods-receipts/trash?${params.toString()}`) as Promise<
      ApiResponse<PharmacyGoodsReceipt[]> & { meta: PaginationMeta }
    >;
  },
  restoreGoodsReceipt: (id: string): Promise<ApiResponse<PharmacyGoodsReceipt>> =>
    post<PharmacyGoodsReceipt>(`/pharmacy/goods-receipts/${id}/restore`),
  permanentDeleteGoodsReceipt: (id: string): Promise<ApiResponse<void>> =>
    del<void>(`/pharmacy/goods-receipts/${id}/permanent`),

  // Supplier invoices / payables
  getInvoices: (filters?: { status?: string; supplierId?: string }): Promise<ApiResponse<PharmacySupplierInvoice[]>> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.supplierId) params.append('supplierId', filters.supplierId);
    const query = params.toString() ? `?${params.toString()}` : '';
    return get<PharmacySupplierInvoice[]>(`/pharmacy/supplier-invoices${query}`);
  },
  getInvoice: (id: string): Promise<ApiResponse<PharmacySupplierInvoice>> =>
    get<PharmacySupplierInvoice>(`/pharmacy/supplier-invoices/${id}`),
  createInvoice: (data: any): Promise<ApiResponse<PharmacySupplierInvoice>> =>
    post<PharmacySupplierInvoice>('/pharmacy/supplier-invoices', data),
  updateInvoice: (id: string, data: any): Promise<ApiResponse<PharmacySupplierInvoice>> =>
    patch<PharmacySupplierInvoice>(`/pharmacy/supplier-invoices/${id}`, data),
  deleteInvoice: (id: string): Promise<ApiResponse<void>> =>
    del<void>(`/pharmacy/supplier-invoices/${id}`),
  bulkDeleteInvoices: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; blocked: string[] }>> =>
    post<{ deleted: string[]; blocked: string[] }>('/pharmacy/supplier-invoices/bulk-delete', { ids }),
  getPayables: (filters?: {
    status?: string;
    supplierId?: string;
    search?: string;
  }): Promise<ApiResponse<{ invoices: PharmacySupplierInvoice[]; totalOutstanding: number; totalOverdue: number }>> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.supplierId) params.append('supplierId', filters.supplierId);
    if (filters?.search) params.append('search', filters.search);
    const query = params.toString() ? `?${params.toString()}` : '';
    return get<{ invoices: PharmacySupplierInvoice[]; totalOutstanding: number; totalOverdue: number }>(
      `/pharmacy/payables${query}`,
    );
  },
  createPayment: (data: {
    invoiceId: string;
    amount: number;
    paidAt?: string;
    method?: PharmacyPaymentMethod;
    reference?: string;
    notes?: string;
  }): Promise<ApiResponse<any>> => post<any>('/pharmacy/supplier-payments', data),

  // Purchase returns (debit notes to suppliers)
  getPurchaseReturns: (filters?: {
    grnId?: string;
    supplierId?: string;
  }): Promise<ApiResponse<PharmacyPurchaseReturn[]>> => {
    const params = new URLSearchParams();
    if (filters?.grnId) params.append('grnId', filters.grnId);
    if (filters?.supplierId) params.append('supplierId', filters.supplierId);
    const query = params.toString() ? `?${params.toString()}` : '';
    return get<PharmacyPurchaseReturn[]>(`/pharmacy/purchase-returns${query}`);
  },
  getPurchaseReturn: (id: string): Promise<ApiResponse<PharmacyPurchaseReturn>> =>
    get<PharmacyPurchaseReturn>(`/pharmacy/purchase-returns/${id}`),
  createPurchaseReturn: (
    grnId: string,
    data: CreatePharmacyPurchaseReturnDto,
  ): Promise<ApiResponse<PharmacyPurchaseReturn>> =>
    post<PharmacyPurchaseReturn>(`/pharmacy/goods-receipts/${grnId}/returns`, data),
};
